import type { BreakoutRoom } from "@/components/call";
import type { DrawingElement } from "@/components/CallWhiteboard";

import type { WsInboundEnvelope } from "./wsInboundEnvelope";
import {
  optBool,
  optCallerId,
  optFiniteNumber,
  optString,
} from "./wsPayloadCoercion";

function scalarForToast(v: unknown): string | undefined {
  const s = optString(v);
  if (s !== undefined) return s;
  if (typeof v === "number" && Number.isFinite(v)) return String(v);
  return undefined;
}

function breakoutRoomsFromUnknown(raw: unknown): BreakoutRoom[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  return raw as BreakoutRoom[];
}

function drawingElementFromUnknown(raw: unknown): DrawingElement | undefined {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) {
    return undefined;
  }
  return raw as DrawingElement;
}

function breakoutParticipantsFromUnknown(
  raw: unknown,
): Array<{ id: number; name: string; avatar?: string }> | undefined {
  if (!Array.isArray(raw)) return undefined;
  return raw as Array<{ id: number; name: string; avatar?: string }>;
}

// --- Parking ---

export interface CallParkedWsPayload {
  type: "call_parked";
  parking_slot?: string;
  parked_by?: string;
}

export function parseCallParkedPayload(
  envelope: WsInboundEnvelope,
): CallParkedWsPayload | null {
  if (envelope.type !== "call_parked") return null;
  const o = envelope;
  return {
    type: "call_parked",
    parking_slot: scalarForToast(o.parking_slot),
    parked_by: optString(o.parked_by),
  };
}

/** No fields; validates event type only. */
export function parseParkedCallRetrievedPayload(
  envelope: WsInboundEnvelope,
): { type: "parked_call_retrieved" } | null {
  if (envelope.type !== "parked_call_retrieved") return null;
  return { type: "parked_call_retrieved" };
}

// --- Recording ---

export interface CallRecordingParticipantWsPayload {
  type: "call_recording_started" | "call_recording_stopped";
  user_id?: number;
  user_name?: string;
}

export function parseCallRecordingStartedPayload(
  envelope: WsInboundEnvelope,
): CallRecordingParticipantWsPayload | null {
  if (envelope.type !== "call_recording_started") return null;
  const o = envelope;
  return {
    type: "call_recording_started",
    user_id: optCallerId(o.user_id),
    user_name: optString(o.user_name),
  };
}

export function parseCallRecordingStoppedPayload(
  envelope: WsInboundEnvelope,
): CallRecordingParticipantWsPayload | null {
  if (envelope.type !== "call_recording_stopped") return null;
  const o = envelope;
  return {
    type: "call_recording_stopped",
    user_id: optCallerId(o.user_id),
    user_name: optString(o.user_name),
  };
}

// --- Whiteboard ---

export interface CallWhiteboardDrawWsPayload {
  type: "call_whiteboard_draw";
  user_id?: number;
  element?: DrawingElement;
}

export function parseCallWhiteboardDrawPayload(
  envelope: WsInboundEnvelope,
): CallWhiteboardDrawWsPayload | null {
  if (envelope.type !== "call_whiteboard_draw") return null;
  const o = envelope;
  return {
    type: "call_whiteboard_draw",
    user_id: optCallerId(o.user_id),
    element: drawingElementFromUnknown(o.element),
  };
}

export interface CallWhiteboardStatusWsPayload {
  type: "call_whiteboard_status";
  user_id?: number;
  user_name?: string;
  /** Legacy payloads may omit or use loosely typed flags; mirrors prior truthiness. */
  is_open: boolean;
}

export function parseCallWhiteboardStatusPayload(
  envelope: WsInboundEnvelope,
): CallWhiteboardStatusWsPayload | null {
  if (envelope.type !== "call_whiteboard_status") return null;
  const o = envelope;
  return {
    type: "call_whiteboard_status",
    user_id: optCallerId(o.user_id),
    user_name: optString(o.user_name),
    is_open: Boolean(o.is_open),
  };
}

// --- Breakout ---

export interface BreakoutRoomsStartedWsPayload {
  type: "breakout_rooms_started";
  rooms?: BreakoutRoom[];
}

export function parseBreakoutRoomsStartedPayload(
  envelope: WsInboundEnvelope,
): BreakoutRoomsStartedWsPayload | null {
  if (envelope.type !== "breakout_rooms_started") return null;
  return {
    type: "breakout_rooms_started",
    rooms: breakoutRoomsFromUnknown(envelope.rooms),
  };
}

/** No fields beyond type (clears breakout state locally). */
export function parseBreakoutRoomsEndedPayload(
  envelope: WsInboundEnvelope,
): { type: "breakout_rooms_ended" } | null {
  if (envelope.type !== "breakout_rooms_ended") return null;
  return { type: "breakout_rooms_ended" };
}

export interface BreakoutRoomUpdatedWsPayload {
  type: "breakout_room_updated";
  room_id?: string;
  participants?: Array<{ id: number; name: string; avatar?: string }>;
}

export function parseBreakoutRoomUpdatedPayload(
  envelope: WsInboundEnvelope,
): BreakoutRoomUpdatedWsPayload | null {
  if (envelope.type !== "breakout_room_updated") return null;
  const o = envelope;
  return {
    type: "breakout_room_updated",
    room_id: optString(o.room_id),
    participants: breakoutParticipantsFromUnknown(o.participants),
  };
}

// --- Participant / lobby ---

export interface CallHandRaiseWsPayload {
  type: "call_hand_raise";
  user_id?: number;
  user_name?: string;
  /** Loose truthiness to match legacy `{ is_raised }` checks on the wire. */
  is_raised: boolean;
}

export function parseCallHandRaisePayload(
  envelope: WsInboundEnvelope,
): CallHandRaiseWsPayload | null {
  if (envelope.type !== "call_hand_raise") return null;
  const o = envelope;
  return {
    type: "call_hand_raise",
    user_id: optCallerId(o.user_id),
    user_name: optString(o.user_name),
    is_raised: Boolean(o.is_raised),
  };
}

export interface CallReactionWsPayload {
  type: "call_reaction";
  reaction_id?: string;
  emoji?: string;
  user_id?: number;
  user_name?: string;
}

export function parseCallReactionPayload(
  envelope: WsInboundEnvelope,
): CallReactionWsPayload | null {
  if (envelope.type !== "call_reaction") return null;
  const o = envelope;
  return {
    type: "call_reaction",
    reaction_id: optString(o.reaction_id),
    emoji: optString(o.emoji),
    user_id: optCallerId(o.user_id),
    user_name: optString(o.user_name),
  };
}

export interface ParticipantMuteRequestWsPayload {
  type: "participant_mute_request";
  target_id?: number;
  requester_name?: string;
}

export function parseParticipantMuteRequestPayload(
  envelope: WsInboundEnvelope,
): ParticipantMuteRequestWsPayload | null {
  if (envelope.type !== "participant_mute_request") return null;
  const o = envelope;
  return {
    type: "participant_mute_request",
    target_id: optCallerId(o.target_id),
    requester_name: optString(o.requester_name),
  };
}

export interface ParticipantRemovedWsPayload {
  type: "participant_removed";
  target_id?: number;
  requester_name?: string;
}

export function parseParticipantRemovedPayload(
  envelope: WsInboundEnvelope,
): ParticipantRemovedWsPayload | null {
  if (envelope.type !== "participant_removed") return null;
  const o = envelope;
  return {
    type: "participant_removed",
    target_id: optCallerId(o.target_id),
    requester_name: optString(o.requester_name),
  };
}

export interface LobbyRequestWsPayload {
  type: "lobby_request";
  meeting_id?: string;
  guest_identity?: string;
  guest_name?: string;
}

export function parseLobbyRequestPayload(
  envelope: WsInboundEnvelope,
): LobbyRequestWsPayload | null {
  if (envelope.type !== "lobby_request") return null;
  const o = envelope;
  return {
    type: "lobby_request",
    meeting_id: optString(o.meeting_id),
    guest_identity: optString(o.guest_identity),
    guest_name: optString(o.guest_name),
  };
}

export interface GuestLeftLobbyWsPayload {
  type: "guest_left_lobby";
  guest_identity?: string;
}

export function parseGuestLeftLobbyPayload(
  envelope: WsInboundEnvelope,
): GuestLeftLobbyWsPayload | null {
  if (envelope.type !== "guest_left_lobby") return null;
  return {
    type: "guest_left_lobby",
    guest_identity: optString(envelope.guest_identity),
  };
}

export interface NewVoicemailWsPayload {
  type: "new_voicemail";
  caller_number?: string;
  duration_seconds?: number;
}

export function parseNewVoicemailPayload(
  envelope: WsInboundEnvelope,
): NewVoicemailWsPayload | null {
  if (envelope.type !== "new_voicemail") return null;
  const o = envelope;
  return {
    type: "new_voicemail",
    caller_number: optString(o.caller_number),
    duration_seconds: optFiniteNumber(o.duration_seconds),
  };
}
